import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../../services/authService';
import { AUTH_SESSION_INVALIDATED_EVENT, clearLegacyAuthStorage } from '../../../services/api';
import { withEnrichedMonitoringArea } from '../../../services/lguMonitoringArea';
import type { User } from '../../../types/auth';
import { COMPUTE_USAGE_EVENT } from '../../../utils/inferenceComputeBudget';
import { homePathForRole } from '../../carbon-projects/homePath';

export type SignupOptions = {
  role: string;
  sub_role?: string;
  organization?: string;
  mobile?: string;
  password_confirmation?: string;
  agree_policy?: boolean;
  monitoring_level?: 'province' | 'municipality' | 'barangay';
  province?: string;
  province_id?: string;
  municipality?: string;
  city_id?: string;
  barangay?: string;
  barangay_id?: string;
};

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (
    name: string,
    email: string,
    password: string,
    options?: SignupOptions
  ) => Promise<{ requiresVerification: boolean }>;
  continueAsGuest: () => Promise<void>;
  verifyEmail: (email: string, otp: string) => Promise<void>;
  resendOTP: (
    email: string,
    options?: {
      password?: string;
      full_name?: string;
      mobile?: string;
      role?: string;
      sub_role?: string;
      organization?: string;
      agree_policy?: boolean;
      monitoring_level?: 'province' | 'municipality' | 'barangay';
      province?: string;
      province_id?: string;
      municipality?: string;
      city_id?: string;
      barangay?: string;
      barangay_id?: string;
    }
  ) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (email: string, otp: string, newPassword: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  /** Bumps when login/guest starts so a stale `/auth/me` from init cannot clear the session. */
  const authSessionGen = useRef(0);

  const publishUser = useCallback(async (userData: User) => {
    const enriched = await withEnrichedMonitoringArea(userData);
    setUser(enriched);
    localStorage.setItem('user', JSON.stringify(enriched));
    return enriched;
  }, []);

  useEffect(() => {
    const onSessionInvalidated = () => {
      setUser(null);
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('user');
      }
      setLoading(false);
    };
    window.addEventListener(AUTH_SESSION_INVALIDATED_EVENT, onSessionInvalidated);
    return () => window.removeEventListener(AUTH_SESSION_INVALIDATED_EVENT, onSessionInvalidated);
  }, []);

  useEffect(() => {
    const initGen = ++authSessionGen.current;

    const initializeAuth = async () => {
      clearLegacyAuthStorage();

      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        JSON.parse(storedUser);
      } catch {
        localStorage.removeItem('user');
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const userData = await authService.getCurrentUser({ skipAuthRefresh: true });
        if (initGen !== authSessionGen.current) return;
        await publishUser(userData);
      } catch (error: unknown) {
        if (initGen !== authSessionGen.current) return;
        const err = error as { response?: { status?: number }; code?: string };
        if (err.code === 'ERR_NETWORK') {
          try {
            setUser(JSON.parse(storedUser));
          } catch {
            localStorage.removeItem('user');
            setUser(null);
          }
          return;
        }
        if (err.response?.status === 401) {
          try {
            await authService.refreshToken();
            const userData = await authService.getCurrentUser({ skipAuthRefresh: true });
            if (initGen !== authSessionGen.current) return;
            await publishUser(userData);
            return;
          } catch {
            if (initGen !== authSessionGen.current) return;
          }
        }
        setUser(null);
        localStorage.removeItem('user');
      } finally {
        if (initGen === authSessionGen.current) {
          setLoading(false);
        }
      }
    };

    initializeAuth();
  }, [publishUser]);

  const login = async (email: string, password: string) => {
    authSessionGen.current += 1;
    await authService.login(email, password);

    const fetchMeWithRetry = async (maxAttempts: number): Promise<User> => {
      let last: unknown;
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          return await authService.getCurrentUser({ skipAuthRefresh: true });
        } catch (e) {
          last = e;
          if (attempt < maxAttempts - 1) {
            await new Promise((r) => setTimeout(r, 350 * (attempt + 1)));
          }
        }
      }
      throw last;
    };

    const userData = await fetchMeWithRetry(3);
    await publishUser(userData);
    setLoading(false);
    const next = sessionStorage.getItem('digisaka_post_login');
    sessionStorage.removeItem('digisaka_post_login');
    const safe =
      next && next.startsWith('/workspace-invite?token=') && !next.includes('//') && !next.includes('\\')
        ? next
        : homePathForRole(userData.role);
    navigate(safe, { replace: true });
  };

  const signup = async (name: string, email: string, password: string, options?: SignupOptions) => {
    await authService.signup({
      email,
      password,
      password_confirmation: options?.password_confirmation ?? password,
      full_name: name,
      mobile: options?.mobile,
      role: options?.role ?? 'blo',
      sub_role: options?.sub_role,
      organization: options?.organization,
      agree_policy: options?.agree_policy ?? true,
      monitoring_level: options?.monitoring_level,
      province: options?.province,
      province_id: options?.province_id,
      municipality: options?.municipality,
      city_id: options?.city_id,
      barangay: options?.barangay,
      barangay_id: options?.barangay_id,
    });

    return { requiresVerification: true };
  };

  const continueAsGuest = async () => {
    authSessionGen.current += 1;
    await authService.guestSession();
    const userData = await authService.getCurrentUser();
    await publishUser(userData);
    setLoading(false);
  };

  const verifyEmail = async (email: string, otp: string) => {
    await authService.verifyEmail({ email, otp });
  };

  const resendOTP = async (
    email: string,
    options?: {
      password?: string;
      full_name?: string;
      mobile?: string;
      role?: string;
      sub_role?: string;
      organization?: string;
      agree_policy?: boolean;
      monitoring_level?: 'province' | 'municipality' | 'barangay';
      province?: string;
      province_id?: string;
      municipality?: string;
      city_id?: string;
      barangay?: string;
      barangay_id?: string;
    }
  ) => {
    await authService.resendVerification({ email, ...options });
  };

  const forgotPassword = async (email: string) => {
    await authService.forgotPassword({ email });
  };

  const resetPassword = async (email: string, otp: string, newPassword: string) => {
    await authService.resetPassword({
      email,
      otp,
      new_password: newPassword,
    });
  };

  const logout = useCallback(() => {
    void authService.logout().finally(() => {
      setUser(null);
      localStorage.removeItem('user');
      navigate('/userdashboard', { replace: true });
    });
  }, [navigate]);

  const refreshUser = useCallback(async () => {
    try {
      const userData = await authService.getCurrentUser();
      await publishUser(userData);
    } catch (error) {
      console.error('Error refreshing user data:', error);
      logout();
    }
  }, [logout, publishUser]);

  const applyLocalComputeUsageMs = useCallback((durationMs: number) => {
    if (!Number.isFinite(durationMs) || durationMs <= 0) return;
    setUser((prev) => {
      if (!prev) return prev;
      if (prev.is_system_guest && prev.guest_quota) {
        const g = prev.guest_quota;
        const used = Math.min(g.budget_ms, g.used_ms + durationMs);
        const remaining = Math.max(0, g.budget_ms - used);
        const next = { ...prev, guest_quota: { ...g, used_ms: used, remaining_ms: remaining } };
        localStorage.setItem('user', JSON.stringify(next));
        return next;
      }
      const mode = (prev.compute_limit_mode || 'daily').toLowerCase();
      if (mode === 'unlimited') return prev;
      const cap =
        mode === 'daily' ? prev.compute_budget_ms_per_day : prev.compute_budget_ms_per_month;
      if (cap == null || cap <= 0) return prev;
      const usedKey = mode === 'daily' ? 'compute_used_ms_today' : 'compute_used_ms_month';
      const curUsed = (prev[usedKey as keyof User] as number) ?? 0;
      const nextUsed = curUsed + durationMs;
      const remaining = Math.max(0, cap - nextUsed);
      const next = {
        ...prev,
        [usedKey]: nextUsed,
        compute_remaining_ms: remaining,
      } as User;
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  useEffect(() => {
    const handler = (ev: Event) => {
      const ms = (ev as CustomEvent<number>).detail;
      if (typeof ms === 'number' && ms > 0) applyLocalComputeUsageMs(ms);
    };
    window.addEventListener(COMPUTE_USAGE_EVENT, handler);
    return () => window.removeEventListener(COMPUTE_USAGE_EVENT, handler);
  }, [applyLocalComputeUsageMs]);

  const value: AuthContextType = {
    user,
    loading,
    login,
    signup,
    continueAsGuest,
    verifyEmail,
    resendOTP,
    forgotPassword,
    resetPassword,
    logout,
    isAuthenticated: !!user,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
